// ============================================
// Auth Types
// ============================================

export interface LoginRequest {
  email: string;
  firebaseToken?: string;
}

export interface ConfirmOTPRequest {
  code: string;
  email: string;
}

export interface RefreshTokenRequest {
  token: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface User {
  id: string;
  email: string;
  role: string;
  authorizationMethod: 'MANUAL' | 'GOOGLE' | 'APPLE';
  enabledNotifications: boolean;
  createdAt: string;
  updatedAt: string;
  subscription?: Subscription;
}

export interface AuthResponse extends User {
  accessToken: string;
  refreshToken: string;
}

// ============================================
// Subscription Types
// ============================================

export type SubscriptionPlan = 'free' | 'cb_199_1m_1w0' | 'cb_299_1m';

// Plan constants for easy checking
export const SUBSCRIPTION_PLANS = {
  FREE: 'free',
  PRO: 'cb_199_1m_1w0',      // 5 signals, USDT only
  MASTER: 'cb_299_1m',        // Unlimited signals, all currencies
} as const;
export type SubscriptionStatus = 'active' | 'cancelled' | 'expired' | 'billing_issue';

export interface Subscription {
  id: string;
  platform: string;
  status: SubscriptionStatus;
  plan: SubscriptionPlan;
  subscriptionId: string | null;
  purchaseToken: string | null;
  expiryDate: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// Signal Types
// ============================================

export type SignalType = 'percent_change' | 'price_threshold' | 'absolute_change';
export type SignalDirection = 'above' | 'below' | 'none';

export interface CreateSignalRequest {
  symbol: string;
  quoteCurrency: string;
  type: SignalType;
  value: number;
  soundName: string;
  direction: SignalDirection[];
  repeat?: boolean;
  active?: boolean;
}

export interface UpdateSignalRequest {
  symbol?: string;
  quoteCurrency?: string;
  type?: SignalType;
  value?: number;
  soundName?: string;
  direction?: SignalDirection[];
  repeat?: boolean;
  active?: boolean;
  isEdit?: boolean;
}

export interface Signal {
  id: string;
  userId: string;
  assetId: string;
  symbol: string;
  quoteCurrency: string;
  type: SignalType;
  value: number;
  startPrice: number;
  startPrices?: Record<string, number>;
  direction: SignalDirection;
  isTriggered: boolean;
  repeat: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  asset?: Asset;
  notificationSignal?: NotificationSignal;
}

export interface NotificationSignal {
  id: string;
  name: string;
  signalId: string;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// Asset Types
// ============================================

export interface Asset {
  id: string;
  symbol: string;
  name: string;
  image: string | null;
  price_change_percentage_24h: number | null;
  market_cap: number | null;
  market_cap_change_percentage_24h: number | null;
  high_24h: number | null;
  low_24h: number | null;
  createdAt: string;
  updatedAt: string;
  prices: AssetPrice[];
}

export interface AssetPrice {
  id: string;
  assetId: string;
  currencyId: string;
  price: number;
  createdAt: string;
  updatedAt: string;
  currency: QuoteCurrency;
}

export interface QuoteCurrency {
  id: string;
  name: string;
  symbol: string;
}

export interface AssetsSearchParams {
  page?: number;
  perPage?: number;
  search?: string;
}

export interface AssetsResponse {
  total: number;
  totalPages: number;
  currentPage: number;
  perPage: number;
  assets: Asset[];
  lastSearchItems: UserAsset[];
}

export interface UserAsset {
  id: string;
  userId: string;
  assetId: string;
  asset: Asset;
  createdAt: string;
  updatedAt: string;
}

// API returns array directly
export type QuoteCurrenciesResponse = QuoteCurrency[] | { currencies: QuoteCurrency[] };

// ============================================
// WebSocket Event Types
// ============================================

export interface SignalCurrentPriceEvent {
  signalId: string;
  assetId: string;
  price: number;
  quoteCurrency: string;
}

export interface NotificationEvent {
  id: string;
  title: string;
  message: string;
  isRead: boolean;
  type?: string;
  direction?: string;
  userId: string;
  soundName?: string;
  createdAt: string;
  signalInfo?: SignalInfo;
}

export interface SignalInfo {
  id: string;
  symbol: string;
  quoteCurrency: string;
  type: SignalType;
  startPrice: number;
  currentPrice: number;
  soundName: string;
}

// ============================================
// API Error Types
// ============================================

export interface ApiError {
  statusCode: number;
  message: string;
  error?: string;
}

// ============================================
// UI Helper Types
// ============================================

export interface SignalFormData {
  asset: Asset | null;
  quoteCurrency: string;
  type: SignalType;
  value: string;
  direction: SignalDirection[];
  repeat: boolean;
}

export const SIGNAL_TYPE_LABELS: Record<SignalType, string> = {
  percent_change: 'Percent Change',
  price_threshold: 'Price Threshold',
  absolute_change: 'Absolute Change',
};

export const DIRECTION_LABELS: Record<SignalDirection, string> = {
  above: 'Above',
  below: 'Below',
  none: 'Any Direction',
};

export const SUBSCRIPTION_LIMITS: Record<string, { maxSignals: number; currencies: string[] }> = {
  free: { maxSignals: 2, currencies: ['USDT'] },
  cb_199_1m_1w0: { maxSignals: 5, currencies: ['USDT'] },
  cb_299_1m: { maxSignals: Infinity, currencies: ['EUR', 'TRY', 'USDT'] },
};
